import { useLastPractice } from '@core/firebase/practice';
import { surahMeta } from '@core/quran/surahMeta';
import { useLanguage } from '@/contexts/LanguageContext';

/**
 * What the system noticed in this pupil's last practice — م-6-02, on the web.
 *
 * The same card as the phone's, and framed the same way: hints from a machine,
 * with the sentence saying so beside them. م-6-03 only holds if the معلم about
 * to mark knows that nothing here has been recorded against the child.
 */
export function PracticeSummary({ studentId }: { studentId: string | null }) {
  const { t, language } = useLanguage();
  const { practice, loading } = useLastPractice(studentId);

  if (!studentId || loading) return null;

  const count = (n: number) => new Intl.NumberFormat('en-US').format(n);
  const surahName = (n: number) => {
    const meta = surahMeta(n);
    if (!meta) return String(n);
    return language === 'en' ? meta.englishName : meta.name;
  };

  if (!practice) {
    return (
      <section
        data-testid="practice-summary"
        className="card-soft p-5"
      >
        <h2 className="font-semibold text-ink">{t('practice.teacherTitle')}</h2>
        <p data-testid="practice-summary-none" className="mt-1 text-sm text-helper">
          {t('practice.teacherNone')}
        </p>
      </section>
    );
  }

  return (
    <section
      data-testid="practice-summary"
      className="card-soft space-y-2 p-5"
    >
      <h2 className="font-semibold text-ink">
        {t('practice.teacherTitle')} · {surahName(practice.surah)} {count(practice.ayah)}
      </h2>

      {practice.unreliable ? (
        <p data-testid="practice-summary-unreliable" className="text-sm text-helper">
          {t('practice.teacherUnreliable')}
        </p>
      ) : (
        <>
          <p data-testid="practice-summary-count" className="text-sm text-ink">
            {t('practice.correctOf', {
              correct: count(practice.correct), total: count(practice.total),
            })}
          </p>
          {practice.weak.length === 0 ? (
            <p data-testid="practice-summary-clean" className="text-sm font-semibold text-success">
              {t('practice.nothingToFix')}
            </p>
          ) : (
            <ul className="space-y-1">
              {practice.weak.map((hint) => (
                <li
                  key={hint.word}
                  data-testid={`practice-weak-${hint.word}`}
                  className="text-sm text-helper"
                >
                  {t('practice.teacherWord', { n: count(hint.word + 1) })} · {hint.expected}
                  {' · '}
                  {hint.kind === 'skipped'
                    ? t('practice.teacherSkipped')
                    : t('practice.teacherChanged', { heard: hint.heard })}
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {/* م-6-03, said to the one reader who could mistake a hint for a mark. */}
      <p data-testid="practice-summary-hint" className="text-xs text-helper">
        {t('practice.teacherHint')}
      </p>
    </section>
  );
}
